import React, { useState, useContext, useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Container, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPen } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";

import { DataContext } from "../../../context/DataContext";
import { getRuta } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import { getRolListAsync } from "../../../api/Rols";
import { getUserByIdAsync, updateUserAsync } from "../../../api/Users";
import { EstadoError, PageHeader, useAviso } from "../../../components/base";
import {
  CargandoFormulario,
  llevarAlPrimerError,
  useCargaLocal,
  useEnvio,
  useEscritura,
} from "../comunes";
import FormularioUsuario, {
  DATOS_VACIOS,
  datosParaServidor,
  useJefes,
  validarUsuario,
} from "./FormularioUsuario";

// Los roles y el usuario llegan juntos: una sola carga con su esqueleto y su "Reintentar".
const cargarTodo = async (token, id) => {
  const [roles, usuario] = await Promise.all([
    getRolListAsync(token),
    getUserByIdAsync(token, id),
  ]);
  if (!roles.statusResponse) return roles;
  if (!usuario.statusResponse) return usuario;
  return { statusResponse: true, data: { roles: roles.data.result, usuario: usuario.data.result } };
};

const EditUser = () => {
  const token = getToken();
  const { id } = useParams();
  let ruta = getRuta();
  let navigate = useNavigate();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const formulario = useRef(null);

  const puedeEditar = tienePermiso(access, "SECURITY UPDATE");

  const [datos, setDatos] = useState(DATOS_VACIOS);
  const [roles, setRoles] = useState([]);
  const [rolActual, setRolActual] = useState("");
  const { jefes, cargandoJefes, errorJefes, cargarJefes } = useJefes(token);

  const [intentado, setIntentado] = useState(false);
  const errores = intentado ? validarUsuario(datos, false, roles) : {};

  const { cargando, error, cargar } = useCargaLocal(() => cargarTodo(token, id));

  const getDatos = async () => {
    const result = await cargar();
    if (!result) return;
    const { roles: listaRoles, usuario } = result.data;
    setRoles(listaRoles);
    setRolActual(usuario.userRol.rolName ?? "");
    setDatos({
      ...DATOS_VACIOS,
      firstName: usuario.firstName ?? "",
      secondName: usuario.secondName ?? "",
      lastName: usuario.lastName ?? "",
      secondLastName: usuario.secondLastName ?? "",
      phone: usuario.phone ?? "",
      address: usuario.address ?? "",
      userName: usuario.userName ?? "",
      selectedRol: usuario.userRol.id,
      selectedJefe: usuario.supervisorId ?? "",
    });
    cargarJefes(usuario.userRol.id);
  };

  useEffect(() => {
    getDatos();
  }, [id]);

  const onCampo = (campo, valor) => setDatos((actual) => ({ ...actual, [campo]: valor }));

  const onRol = (idRol) => {
    setDatos((actual) => ({ ...actual, selectedRol: idRol, selectedJefe: "" }));
    cargarJefes(idRol);
  };

  const saveChanges = () =>
    enviar(async () => {
      setIntentado(true);
      if (Object.keys(validarUsuario(datos, false, roles)).length > 0) {
        llevarAlPrimerError(formulario);
        return;
      }
      const ok = await escribir(
        () => updateUserAsync(token, { id, ...datosParaServidor(datos) }),
        "No se pudo actualizar al usuario"
      );
      if (!ok) return;
      setIntentado(false);
      aviso.exito("Usuario actualizado");
    });

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo={puedeEditar ? "Editar usuario" : "Datos del usuario"}
        subtitulo={
          datos.userName && !cargando ? `Usuario de acceso: ${datos.userName}` : undefined
        }
        icono={<FontAwesomeIcon icon={faUserPen} />}
        acciones={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`${ruta}/users`)}
          >
            Regresar
          </Button>
        }
      />

      {error ? (
        <EstadoError
          titulo={
            error === "conexion"
              ? "No se pudo conectar con el servidor"
              : "No se pudo cargar el usuario"
          }
          mensaje="Revisa tu conexión e inténtalo de nuevo."
          onReintentar={getDatos}
        />
      ) : cargando ? (
        <CargandoFormulario etiqueta="Cargando el usuario…" />
      ) : (
        <Stack spacing={3} ref={formulario}>
          <FormularioUsuario
            datos={datos}
            onCampo={onCampo}
            onRol={onRol}
            errores={errores}
            esAlta={false}
            deshabilitado={guardando || !puedeEditar}
            roles={roles}
            estadoJefes={{ jefes, cargandoJefes, errorJefes }}
            rolActual={rolActual}
          />

          {puedeEditar ? (
            <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
              <Button
                variant="contained"
                size="large"
                disabled={guardando}
                onClick={() => saveChanges()}
                startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 220 }}
              >
                {guardando ? "Guardando…" : "Guardar cambios"}
              </Button>
            </Box>
          ) : null}
        </Stack>
      )}
    </Container>
  );
};

export default EditUser;
